import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/api/client";

/**
 * Follow-up popup that fires AFTER a task is marked complete. Captures:
 *   1) Hours worked today on this task — ADDS to the running timer/counter
 *      (POST /tasks/{id}/time/manual, decimals allowed, capped at 12h)
 *   2) Optional NAS workfile path + final video/design link — PATCHed onto
 *      the task if either is filled.
 *
 * Fires on every completion (even quick 5-min tasks) so daily hours are
 * captured per task per user.
 *
 * Props:
 *   task          — the freshly-completed task
 *   open          — controlled boolean
 *   onClose(updatedTaskOrNull) — called when the user closes
 */
export default function CompleteLinksDialog({ task, open, onClose }) {
  const [hours, setHours] = useState("");
  const [nas, setNas] = useState("");
  const [finalLink, setFinalLink] = useState("");
  const [skip, setSkip] = useState(false);
  const [saving, setSaving] = useState(false);

  // Carry-forward mode: task isn't done yet, so we only ask for hours and
  // hide the NAS / final-link section. `__popup_mode` is set by the callers
  // (MyTasks / Whiteboard) when they open us on a "carry_forward" transition.
  const carryMode = task?.__popup_mode === "carry_forward";

  useEffect(() => {
    if (open && task) {
      setHours("");
      setNas(task.nas_link || "");
      setFinalLink(task.final_video_link || "");
      setSkip(false);
    }
  }, [open, task]);

  if (!task) return null;

  const anyFilled = hours.trim() || nas.trim() || finalLink.trim();
  // Accept dots AND commas as decimal separators — Indian keyboards commonly
  // emit "," and JS parseFloat only groks ".".
  const parsedHours = parseFloat(String(hours ?? "").trim().replace(",", "."));
  const hoursValid = !hours.trim() || (Number.isFinite(parsedHours) && parsedHours > 0 && parsedHours <= 12);

  const save = async () => {
    if (!hoursValid) {
      toast.error("Hours must be a positive number, max 12");
      return;
    }
    setSaving(true);
    let updatedTask = null;
    try {
      // 1) Log the hours (adds to the ledger — does NOT overwrite the timer)
      if (hours.trim() && !isNaN(parsedHours) && parsedHours > 0) {
        try {
          await api.post(`/tasks/${task.id}/time/manual`, {
            hours: parsedHours,
            note: "Logged on task completion",
          });
          toast.success(`+${parsedHours}h logged on this task`);
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Failed to log hours");
        }
      }
      // 2) Attach delivery links if any changed
      const nasChanged = nas.trim() !== (task.nas_link || "").trim();
      const finalChanged = finalLink.trim() !== (task.final_video_link || "").trim();
      if (!skip && (nasChanged || finalChanged)) {
        try {
          const { data } = await api.patch(`/tasks/${task.id}`, {
            nas_link: nas.trim(),
            final_video_link: finalLink.trim(),
          });
          updatedTask = data;
          toast.success("Delivery links attached");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Failed to attach links");
        }
      }
      onClose?.(updatedTask);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(null); }}>
      <DialogContent className={`bg-surface text-white max-w-md ${carryMode ? "border-cyan-500/30" : "border-emerald-500/30"}`} data-testid="complete-links-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <CheckCircle2 size={18} className={carryMode ? "text-cyan-300" : "text-emerald-300"} />
            {carryMode ? "Carry forward — log today's hours" : "Task completed — wrap up"}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-xs leading-relaxed">
            {carryMode ? (
              <>Nice progress on <span className="text-white/90">&quot;{task.title}&quot;</span>. Log the hours you spent today so it lands in your daily total — the task will automatically roll onto tomorrow's board.</>
            ) : (
              <>Nice work on <span className="text-white/90">&quot;{task.title}&quot;</span>. Log the hours you spent today and (optionally) drop delivery links so teammates can grab the file later.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="overline flex items-center gap-1.5">
              <Clock size={11} /> Hours worked today on this task
            </Label>
            <Input
              autoFocus
              type="text"
              inputMode="decimal"
              data-testid="complete-hours-input"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="e.g. 1.5"
              className={`bg-black/40 border-white/10 mt-1.5 focus-visible:ring-emerald-500 ${!hoursValid ? "border-red-500/60" : ""}`}
            />
            <div className="text-[0.7rem] text-white/40 font-mono mt-1">
              Adds to the running timer total for this task. Decimals allowed (e.g. 0.75). Max 12 h.
            </div>
          </div>
          <div>
            <Label className="overline">NAS Workfile URL (optional)</Label>
            <Input
              data-testid="complete-links-nas"
              value={nas}
              onChange={(e) => setNas(e.target.value)}
              disabled={skip || carryMode}
              placeholder="//nas/projects/…"
              className={`bg-black/40 border-white/10 mt-1.5 focus-visible:ring-emerald-500 disabled:opacity-40 ${carryMode ? "hidden" : ""}`}
            />
          </div>
          <div className={carryMode ? "hidden" : ""}>
            <Label className="overline">Final Video / Design Link (optional)</Label>
            <Input
              data-testid="complete-links-final"
              value={finalLink}
              onChange={(e) => setFinalLink(e.target.value)}
              disabled={skip || carryMode}
              placeholder="Drive / Wistia / Vimeo / Figma…"
              className="bg-black/40 border-white/10 mt-1.5 focus-visible:ring-emerald-500 disabled:opacity-40"
            />
          </div>
          <label className={`items-center gap-2 pt-1 cursor-pointer select-none ${carryMode ? "hidden" : "flex"}`}>
            <Checkbox
              data-testid="complete-links-skip"
              checked={skip}
              onCheckedChange={(v) => setSkip(!!v)}
              className="border-white/30 data-[state=checked]:bg-amber-500/40 data-[state=checked]:border-amber-500/60"
            />
            <span className="text-xs text-white/60">Delivery links not required for this task</span>
          </label>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose?.(null)}
            className="border-white/15"
            data-testid="complete-links-skip-btn"
          >{carryMode ? "Cancel" : "Skip everything"}</Button>
          <Button
            onClick={save}
            disabled={saving || !hoursValid}
            data-testid="complete-links-save"
            className={`${carryMode ? "bg-cyan-600 hover:bg-cyan-700" : "bg-emerald-600 hover:bg-emerald-700"} disabled:opacity-50`}
          >
            {saving ? "Saving…" : anyFilled ? "Save & Close" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
